import { useState, useEffect } from "react";
import axios from "axios";
import { API, getAuthHeader, formatDateDDMMYYYY, getTodayDDMMYYYY, fromInputDateFormat } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Plus, CheckCircle2, Clock, AlertCircle, Trash2, Search, Settings, Bell, Edit, X } from "lucide-react";

const DEFAULT_CATEGORIES = [
  { value: "general", label: "General", color: "bg-slate-100 text-slate-700 border-slate-200" },
  { value: "bug", label: "Bug", color: "bg-red-100 text-red-700 border-red-200" },
  { value: "feature", label: "Feature", color: "bg-blue-100 text-blue-700 border-blue-200" },
  { value: "documentation", label: "Documentation", color: "bg-purple-100 text-purple-700 border-purple-200" },
  { value: "urgent", label: "Urgent", color: "bg-orange-100 text-orange-700 border-orange-200" },
  { value: "meeting", label: "Meeting", color: "bg-green-100 text-green-700 border-green-200" }
];

const DEFAULT_TEAMS = [
  { value: "Account", label: "Account Team" },
  { value: "Processing", label: "Processing Team" },
  { value: "BackOffice", label: "Back Office Team" },
  { value: "ITDept", label: "IT Department" },
  { value: "Partners", label: "Partners Team" }
];

const CATEGORY_COLORS = [
  "bg-slate-100 text-slate-700 border-slate-200",
  "bg-red-100 text-red-700 border-red-200",
  "bg-blue-100 text-blue-700 border-blue-200",
  "bg-purple-100 text-purple-700 border-purple-200",
  "bg-orange-100 text-orange-700 border-orange-200",
  "bg-green-100 text-green-700 border-green-200",
  "bg-pink-100 text-pink-700 border-pink-200",
  "bg-yellow-100 text-yellow-700 border-yellow-200",
  "bg-teal-100 text-teal-700 border-teal-200",
  "bg-indigo-100 text-indigo-700 border-indigo-200"
];

const TasksPanel = ({ user }) => {
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  
  // Search and filters
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterTeam, setFilterTeam] = useState("all");
  const [filterUser, setFilterUser] = useState("all");
  const [filterCategory, setFilterCategory] = useState("all");
  const [sortBy, setSortBy] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("desc");
  
  // Categories and Teams (admin editable)
  const [categories, setCategories] = useState(() => {
    const saved = localStorage.getItem('taskCategories');
    return saved ? JSON.parse(saved) : DEFAULT_CATEGORIES;
  });
  const [teams, setTeams] = useState(() => {
    const saved = localStorage.getItem('taskTeams');
    return saved ? JSON.parse(saved) : DEFAULT_TEAMS;
  });
  
  // Settings form
  const [newCategory, setNewCategory] = useState({ value: "", label: "", color: CATEGORY_COLORS[0] });
  const [newTeam, setNewTeam] = useState({ value: "", label: "" });
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingTeam, setEditingTeam] = useState(null);
  
  // New task form
  const [newTask, setNewTask] = useState({
    title: "",
    description: "",
    team: user?.team || "",
    assigned_to: "",
    status: "pending",
    priority: "medium",
    category: "general",
    due_date: "",
    created_date: getTodayDDMMYYYY(),
    reminder_enabled: false,
    reminder_date: "",
    reminder_time: "09:00"
  });

  useEffect(() => {
    fetchData();
  }, [sortBy, sortOrder]);

  // Save categories and teams to localStorage when they change
  useEffect(() => {
    localStorage.setItem('taskCategories', JSON.stringify(categories));
  }, [categories]);

  useEffect(() => {
    localStorage.setItem('taskTeams', JSON.stringify(teams));
  }, [teams]);

  const fetchData = async () => {
    try {
      const [tasksRes, usersRes] = await Promise.all([
        axios.get(`${API}/tasks?sort_by=${sortBy}&sort_order=${sortOrder}`, { headers: getAuthHeader() }),
        axios.get(`${API}/users`, { headers: getAuthHeader() })
      ]);
      setTasks(tasksRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      toast.error("Failed to fetch tasks");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    try {
      const assignedUser = users.find(u => u.id === newTask.assigned_to);
      const taskData = {
        ...newTask,
        assigned_to_name: assignedUser ? assignedUser.name : null,
        team: newTask.team || user?.team
      };
      await axios.post(`${API}/tasks`, taskData, { headers: getAuthHeader() });
      toast.success("Task created successfully");
      setCreateDialogOpen(false);
      resetNewTaskForm();
      fetchData();
    } catch (error) {
      toast.error("Failed to create task");
    }
  };

  const resetNewTaskForm = () => {
    setNewTask({
      title: "",
      description: "",
      team: user?.team || "",
      assigned_to: "",
      status: "pending",
      priority: "medium",
      category: "general",
      due_date: "",
      created_date: getTodayDDMMYYYY(),
      reminder_enabled: false,
      reminder_date: "",
      reminder_time: "09:00"
    });
  };

  const handleUpdateStatus = async (taskId, newStatus) => {
    try {
      await axios.put(`${API}/tasks/${taskId}`, { status: newStatus }, { headers: getAuthHeader() });
      toast.success("Task updated");
      fetchData();
    } catch (error) {
      toast.error("Failed to update task");
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm("Are you sure you want to delete this task?")) return;
    try {
      await axios.delete(`${API}/tasks/${taskId}`, { headers: getAuthHeader() });
      toast.success("Task deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete task");
    }
  };

  // Category management (admin only)
  const handleAddCategory = () => {
    if (!newCategory.value || !newCategory.label) {
      toast.error("Please fill in category name");
      return;
    }
    const value = newCategory.value.toLowerCase().replace(/\s+/g, '_');
    if (categories.some(c => c.value === value)) {
      toast.error("Category already exists");
      return;
    }
    setCategories([...categories, { ...newCategory, value }]);
    setNewCategory({ value: "", label: "", color: CATEGORY_COLORS[0] });
    toast.success("Category added");
  };

  const handleDeleteCategory = (value) => {
    if (categories.length <= 1) {
      toast.error("Must have at least one category");
      return;
    }
    setCategories(categories.filter(c => c.value !== value));
    toast.success("Category deleted");
  };

  // Team management (admin only)
  const handleAddTeam = () => {
    if (!newTeam.value || !newTeam.label) {
      toast.error("Please fill in team name");
      return;
    }
    const value = newTeam.value.replace(/\s+/g, '');
    if (teams.some(t => t.value === value)) {
      toast.error("Team already exists");
      return;
    }
    setTeams([...teams, { value, label: newTeam.label }]);
    setNewTeam({ value: "", label: "" });
    toast.success("Team added");
  };

  const handleDeleteTeam = (value) => {
    if (teams.length <= 1) {
      toast.error("Must have at least one team");
      return;
    }
    setTeams(teams.filter(t => t.value !== value));
    toast.success("Team deleted");
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "completed": return <CheckCircle2 className="w-4 h-4" />;
      case "in_progress": return <Clock className="w-4 h-4" />;
      default: return <AlertCircle className="w-4 h-4" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "completed": return "bg-green-100 text-green-700 border-green-200";
      case "in_progress": return "bg-blue-100 text-blue-700 border-blue-200";
      default: return "bg-amber-100 text-amber-700 border-amber-200";
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "high": return "bg-red-100 text-red-700 border-red-200";
      case "medium": return "bg-orange-100 text-orange-700 border-orange-200";
      default: return "bg-slate-100 text-slate-700 border-slate-200";
    }
  };

  const getCategoryColor = (category) => {
    const cat = categories.find(c => c.value === category);
    return cat ? cat.color : "bg-slate-100 text-slate-700 border-slate-200";
  };

  // Filter tasks based on search and filters
  const filteredTasks = tasks.filter(task => {
    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      const matchesSearch = 
        task.title?.toLowerCase().includes(search) ||
        task.description?.toLowerCase().includes(search) ||
        task.assigned_to_name?.toLowerCase().includes(search) ||
        task.team?.toLowerCase().includes(search) ||
        task.category?.toLowerCase().includes(search) ||
        task.status?.toLowerCase().includes(search) ||
        task.priority?.toLowerCase().includes(search);
      if (!matchesSearch) return false;
    }
    
    if (filterStatus !== "all" && task.status !== filterStatus) return false;
    if (user?.role === "admin" && filterTeam !== "all" && task.team !== filterTeam) return false;
    if (filterUser !== "all" && task.assigned_to !== filterUser) return false;
    if (filterCategory !== "all" && task.category !== filterCategory) return false;
    return true;
  });

  // Separate tasks into active and completed
  const activeTasks = filteredTasks.filter(task => task.status !== 'completed');
  const completedTasks = filteredTasks.filter(task => task.status === 'completed');

  // Get users filtered by selected team (for filters)
  const filteredUsers = filterTeam !== "all" 
    ? users.filter(u => u.team === filterTeam)
    : users;

  // Get users filtered by selected team in create form
  const teamUsersForCreate = newTask.team 
    ? users.filter(u => u.team === newTask.team)
    : users;

  // Task card component
  const TaskCard = ({ task, isCompleted = false }) => (
    <div 
      key={task.id} 
      data-testid={`task-item-${task.id}`} 
      className={`p-4 rounded-xl border transition-all ${
        isCompleted 
          ? 'border-slate-200 bg-slate-100/50 opacity-75' 
          : 'border-slate-200 bg-slate-50 hover:shadow-md'
      }`}
    >
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div className="flex-1">
          <div className="flex items-start gap-3">
            <div className="flex-1">
              <h3 className={`font-semibold mb-1 ${isCompleted ? 'line-through text-slate-500' : 'text-slate-900'}`}>
                {task.title}
              </h3>
              <p className={`text-sm mb-2 ${isCompleted ? 'line-through text-slate-400' : 'text-slate-600'}`}>
                {task.description}
              </p>
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <Badge className={`${getStatusColor(task.status)} border`}>
                  {getStatusIcon(task.status)}
                  <span className="ml-1">{task.status.replace('_', ' ')}</span>
                </Badge>
                <Badge className={`${getPriorityColor(task.priority)} border ${isCompleted ? 'opacity-60' : ''}`}>
                  {task.priority}
                </Badge>
                <Badge className={`${getCategoryColor(task.category)} border ${isCompleted ? 'opacity-60' : ''}`}>
                  {categories.find(c => c.value === task.category)?.label || task.category}
                </Badge>
                {user?.role === 'admin' && (
                  <Badge variant="outline" className={`border-slate-300 ${isCompleted ? 'opacity-60' : ''}`}>
                    {task.team}
                  </Badge>
                )}
                {task.assigned_to_name && (
                  <span className={isCompleted ? 'text-slate-400' : 'text-slate-600'}>→ {task.assigned_to_name}</span>
                )}
                {task.due_date && (
                  <span className={isCompleted ? 'text-slate-400' : 'text-slate-600'}>📅 {formatDateDDMMYYYY(task.due_date)}</span>
                )}
                {task.reminder_enabled && (
                  <Bell className={`w-3 h-3 ${isCompleted ? 'text-slate-400' : 'text-orange-500'}`} />
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Select onValueChange={(value) => handleUpdateStatus(task.id, value)} value={task.status}>
            <SelectTrigger className="w-32 h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant="ghost"
            size="sm"
            data-testid={`delete-task-${task.id}`}
            onClick={() => handleDeleteTask(task.id)}
            className="text-red-600 hover:text-red-700 hover:bg-red-50 h-9 w-9 p-0"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading tasks...</div>;
  }

  return (
    <div className="space-y-6">
      <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg">
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <CardTitle style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Tasks</CardTitle>
              <CardDescription>Manage and track team tasks</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              {user?.role === 'admin' && (
                <Button 
                  variant="outline"
                  onClick={() => setSettingsDialogOpen(true)}
                  className="h-10"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Manage Categories
                </Button>
              )}
              <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
                <DialogTrigger asChild>
                  <Button 
                    data-testid="create-task-button"
                    className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white shadow-lg"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create Task
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Create New Task</DialogTitle>
                    <DialogDescription>Assign a task to a team member</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleCreateTask} className="space-y-4">
                    {/* Created Date - Auto filled */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Created Date</Label>
                        <Input
                          type="text"
                          value={newTask.created_date}
                          disabled
                          className="bg-slate-50"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Due Date</Label>
                        <Input
                          type="text"
                          placeholder="dd/mm/yyyy"
                          value={newTask.due_date}
                          onChange={(e) => setNewTask({ ...newTask, due_date: e.target.value })}
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="title">Task Title *</Label>
                      <Input
                        id="title"
                        data-testid="task-title-input"
                        placeholder="Enter task title"
                        value={newTask.title}
                        onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="description">Description</Label>
                      <Textarea
                        id="description"
                        placeholder="Task description"
                        value={newTask.description}
                        onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                        rows={2}
                      />
                    </div>

                    {/* Team Selection */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Team *</Label>
                        <Select 
                          value={newTask.team} 
                          onValueChange={(val) => setNewTask({ ...newTask, team: val, assigned_to: "" })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select team" />
                          </SelectTrigger>
                          <SelectContent>
                            {teams.map(team => (
                              <SelectItem key={team.value} value={team.value}>{team.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Assign To - Filtered by Team */}
                      <div className="space-y-2">
                        <Label>Assign To</Label>
                        <Select 
                          value={newTask.assigned_to || "unassigned"} 
                          onValueChange={(val) => setNewTask({ ...newTask, assigned_to: val === "unassigned" ? "" : val })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select member" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="unassigned">Unassigned</SelectItem>
                            {teamUsersForCreate.map(u => (
                              <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label>Priority</Label>
                        <Select value={newTask.priority} onValueChange={(val) => setNewTask({ ...newTask, priority: val })}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="low">Low</SelectItem>
                            <SelectItem value="medium">Medium</SelectItem>
                            <SelectItem value="high">High</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label>Category</Label>
                        <Select value={newTask.category} onValueChange={(val) => setNewTask({ ...newTask, category: val })}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {categories.map(cat => (
                              <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label>Status</Label>
                        <Select value={newTask.status} onValueChange={(val) => setNewTask({ ...newTask, status: val })}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pending">Pending</SelectItem>
                            <SelectItem value="in_progress">In Progress</SelectItem>
                            <SelectItem value="completed">Completed</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Reminder Section */}
                    <div className="space-y-3 p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center justify-between">
                        <Label className="flex items-center gap-2">
                          <Bell className="w-4 h-4 text-orange-500" />
                          Set Reminder
                        </Label>
                        <Switch
                          checked={newTask.reminder_enabled}
                          onCheckedChange={(checked) => setNewTask({ ...newTask, reminder_enabled: checked })}
                        />
                      </div>
                      
                      {newTask.reminder_enabled && (
                        <div className="grid grid-cols-2 gap-3 mt-2">
                          <div className="space-y-1">
                            <Label className="text-xs">Reminder Date</Label>
                            <Input
                              type="text"
                              placeholder="dd/mm/yyyy"
                              value={newTask.reminder_date}
                              onChange={(e) => setNewTask({ ...newTask, reminder_date: e.target.value })}
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Reminder Time</Label>
                            <Input
                              type="time"
                              value={newTask.reminder_time}
                              onChange={(e) => setNewTask({ ...newTask, reminder_time: e.target.value })}
                            />
                          </div>
                        </div>
                      )}
                    </div>

                    <DialogFooter>
                      <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>
                        Cancel
                      </Button>
                      <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                        Create Task
                      </Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          {/* Search Bar */}
          <div className="mt-4">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Search tasks by title, description, assignee, team, category..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-white"
              />
              {searchTerm && (
                <button 
                  onClick={() => setSearchTerm("")}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3 mt-4 text-sm">
            <span className="text-slate-600 font-medium">Filters:</span>
            
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-600">Status:</span>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-32 h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-600">Category:</span>
              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger className="w-36 h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {user?.role === 'admin' && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-600">Team:</span>
                <Select value={filterTeam} onValueChange={(val) => { setFilterTeam(val); setFilterUser("all"); }}>
                  <SelectTrigger className="w-40 h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Teams</SelectItem>
                    {teams.map(team => (
                      <SelectItem key={team.value} value={team.value}>{team.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-600">User:</span>
              <Select value={filterUser} onValueChange={setFilterUser}>
                <SelectTrigger className="w-40 h-9">
                  <SelectValue placeholder="All Users" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Users</SelectItem>
                  {filteredUsers.map(u => (
                    <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="border-l border-slate-300 pl-2 ml-2"></div>

            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-slate-700">Sort:</span>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-36 h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="created_at">Created Date</SelectItem>
                  <SelectItem value="due_date">Due Date</SelectItem>
                  <SelectItem value="priority">Priority</SelectItem>
                  <SelectItem value="status">Status</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Select value={sortOrder} onValueChange={setSortOrder}>
              <SelectTrigger className="w-32 h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="desc">Newest First</SelectItem>
                <SelectItem value="asc">Oldest First</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {filteredTasks.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-8 h-8 text-slate-400" />
              </div>
              <p className="text-slate-500">
                {searchTerm ? `No tasks found for "${searchTerm}"` : "No tasks found"}
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Active Tasks */}
              {activeTasks.length > 0 && (
                <div className="space-y-3">
                  {filterStatus === "all" && (
                    <div className="flex items-center gap-2 mb-2">
                      <Clock className="w-4 h-4 text-blue-600" />
                      <h4 className="font-semibold text-slate-700">Active Tasks ({activeTasks.length})</h4>
                    </div>
                  )}
                  {activeTasks.map(task => (
                    <TaskCard key={task.id} task={task} isCompleted={false} />
                  ))}
                </div>
              )}

              {/* Completed Tasks Section */}
              {completedTasks.length > 0 && filterStatus !== "completed" && filterStatus !== "pending" && filterStatus !== "in_progress" && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 pt-4 border-t border-slate-200">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    <h4 className="font-semibold text-slate-500">Completed Tasks ({completedTasks.length})</h4>
                  </div>
                  {completedTasks.map(task => (
                    <TaskCard key={task.id} task={task} isCompleted={true} />
                  ))}
                </div>
              )}

              {/* When filtering by completed status only */}
              {filterStatus === "completed" && completedTasks.length > 0 && (
                <div className="space-y-3">
                  {completedTasks.map(task => (
                    <TaskCard key={task.id} task={task} isCompleted={true} />
                  ))}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Settings Dialog - Admin Only */}
      <Dialog open={settingsDialogOpen} onOpenChange={setSettingsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Manage Categories & Teams</DialogTitle>
            <DialogDescription>Add, edit, or remove task categories and teams</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Categories Section */}
            <div className="space-y-4">
              <h3 className="font-semibold text-lg">Categories</h3>
              
              {/* Add New Category */}
              <div className="flex items-end gap-2 p-3 bg-slate-50 rounded-lg">
                <div className="flex-1 space-y-1">
                  <Label className="text-xs">Category Name</Label>
                  <Input
                    placeholder="e.g., Review"
                    value={newCategory.label}
                    onChange={(e) => setNewCategory({ 
                      ...newCategory, 
                      label: e.target.value, 
                      value: e.target.value.toLowerCase().replace(/\s+/g, '_') 
                    })}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Color</Label>
                  <Select 
                    value={newCategory.color} 
                    onValueChange={(val) => setNewCategory({ ...newCategory, color: val })}
                  >
                    <SelectTrigger className="w-40">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORY_COLORS.map((color, idx) => (
                        <SelectItem key={idx} value={color}>
                          <div className="flex items-center gap-2">
                            <div className={`w-4 h-4 rounded ${color.split(' ')[0]}`}></div>
                            <span>Color {idx + 1}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={handleAddCategory} size="sm">
                  <Plus className="w-4 h-4 mr-1" /> Add
                </Button>
              </div>

              {/* Existing Categories */}
              <div className="space-y-2">
                {categories.map(cat => (
                  <div key={cat.value} className="flex items-center justify-between p-2 bg-white border rounded-lg">
                    <div className="flex items-center gap-2">
                      <Badge className={`${cat.color} border`}>{cat.label}</Badge>
                      <span className="text-xs text-slate-500">({cat.value})</span>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => handleDeleteCategory(cat.value)}
                      className="text-red-600 hover:text-red-700 h-8 w-8 p-0"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            {/* Teams Section */}
            <div className="space-y-4 border-t pt-4">
              <h3 className="font-semibold text-lg">Teams</h3>
              
              {/* Add New Team */}
              <div className="flex items-end gap-2 p-3 bg-slate-50 rounded-lg">
                <div className="flex-1 space-y-1">
                  <Label className="text-xs">Team Code</Label>
                  <Input
                    placeholder="e.g., Marketing"
                    value={newTeam.value}
                    onChange={(e) => setNewTeam({ ...newTeam, value: e.target.value.replace(/\s+/g, '') })}
                  />
                </div>
                <div className="flex-1 space-y-1">
                  <Label className="text-xs">Display Name</Label>
                  <Input
                    placeholder="e.g., Marketing Team"
                    value={newTeam.label}
                    onChange={(e) => setNewTeam({ ...newTeam, label: e.target.value })}
                  />
                </div>
                <Button onClick={handleAddTeam} size="sm">
                  <Plus className="w-4 h-4 mr-1" /> Add
                </Button>
              </div>

              {/* Existing Teams */}
              <div className="space-y-2">
                {teams.map(team => (
                  <div key={team.value} className="flex items-center justify-between p-2 bg-white border rounded-lg">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">{team.label}</Badge>
                      <span className="text-xs text-slate-500">({team.value})</span>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => handleDeleteTeam(team.value)}
                      className="text-red-600 hover:text-red-700 h-8 w-8 p-0"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setSettingsDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TasksPanel;
